import { defineStore } from 'pinia'
import {
  createDefaultAnswers,
  defaultModulesFor,
  normalizeModules,
  type BusinessType,
  type OnboardingAnswers,
} from '~/types/onboarding'
import type { SiteSchema } from '~/types/site'
import { asBuilderSiteData } from '~/types/site'
import { buildSchemaFromOnboarding } from '~/utils/templates'
import { validateSiteSchema } from '~/utils/validateSiteSchema'
import { useBuilderStore } from '~/stores/useBuilderStore'
import {
  DEFAULT_THEME_NEUTRAL,
  DEFAULT_THEME_PRIMARY,
  normalizeThemeNeutral,
  normalizeThemePrimary,
  primaryFromLegacyHex,
} from '~/composables/useBuilderMeta'

export const SITE_DRAFT_KEY = 'aradcore:site-draft'
export const ONBOARDING_ANSWERS_KEY = 'aradcore:onboarding-answers'
export const ONBOARDING_COMPLETE_KEY = 'aradcore:onboarding-complete'
export const NEW_SITE_KEY = 'aradcore:new-site'

export function isNewSiteFlow(): boolean {
  return import.meta.client && sessionStorage.getItem(NEW_SITE_KEY) === '1'
}

interface OnboardingState {
  answers: OnboardingAnswers
  furthestStep: number
  completed: boolean
  hydrated: boolean
}

function readJson<T>(key: string): T | null {
  if (!import.meta.client) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function writeJson(key: string, value: unknown): void {
  if (!import.meta.client) return
  localStorage.setItem(key, JSON.stringify(value))
}

export const useOnboardingStore = defineStore('onboarding', {
  state: (): OnboardingState => ({
    answers: createDefaultAnswers(),
    furthestStep: 1,
    completed: false,
    hydrated: false,
  }),

  getters: {
    schema(state): SiteSchema {
      return buildSchemaFromOnboarding(state.answers)
    },

    hasDraft(): boolean {
      if (!import.meta.client) return this.completed
      return this.completed || !!localStorage.getItem(SITE_DRAFT_KEY)
    },
  },

  actions: {
    hydrateFromStorage() {
      if (!import.meta.client) return
      const answers = readJson<OnboardingAnswers>(ONBOARDING_ANSWERS_KEY)
      if (answers) {
        const businessType = answers.businessType ?? 'solo'
        const legacy = answers as OnboardingAnswers & { themeColor?: string }
        this.answers = {
          ...createDefaultAnswers(),
          ...answers,
          businessType,
          themePrimary: normalizeThemePrimary(
            legacy.themePrimary
            || primaryFromLegacyHex(legacy.themeColor)
            || DEFAULT_THEME_PRIMARY,
          ),
          themeNeutral: normalizeThemeNeutral(legacy.themeNeutral),
          modules: normalizeModules(
            {
              ...defaultModulesFor(businessType),
              ...(answers.modules ?? {}),
            },
            businessType,
          ),
        }
      }
      this.completed = localStorage.getItem(ONBOARDING_COMPLETE_KEY) === '1'
      // Wizard is 4 steps; clamp leftover furthestStep from older drafts.
      if (this.furthestStep > 4) this.furthestStep = 4
      this.hydrated = true
    },

    persistAnswers() {
      writeJson(ONBOARDING_ANSWERS_KEY, this.answers)
    },

    setBusinessType(type: BusinessType) {
      this.answers.businessType = type
      this.answers.modules = defaultModulesFor(type)
      this.persistAnswers()
    },

    patchAnswers(partial: Partial<OnboardingAnswers>) {
      const next = { ...this.answers, ...partial }
      if (partial.modules) {
        next.modules = normalizeModules(partial.modules, next.businessType)
      }
      this.answers = next
      this.persistAnswers()
    },

    setFurthestStep(step: number) {
      const capped = Math.min(Math.max(1, step), 4)
      if (capped > this.furthestStep) this.furthestStep = capped
    },

    reset() {
      this.answers = createDefaultAnswers()
      this.furthestStep = 1
      this.completed = false
      if (import.meta.client) {
        localStorage.removeItem(SITE_DRAFT_KEY)
        localStorage.removeItem(ONBOARDING_ANSWERS_KEY)
        localStorage.removeItem(ONBOARDING_COMPLETE_KEY)
        sessionStorage.removeItem(NEW_SITE_KEY)
      }
      const builder = useBuilderStore()
      builder.siteData = null
      builder.markClean()
    },

    /**
     * Validate schema, persist to localStorage + builder store, mark complete.
     */
    commit(): SiteSchema {
      const schema = this.schema
      validateSiteSchema(schema)
      writeJson(SITE_DRAFT_KEY, schema)
      writeJson(ONBOARDING_ANSWERS_KEY, this.answers)
      if (import.meta.client) {
        localStorage.setItem(ONBOARDING_COMPLETE_KEY, '1')
      }
      this.completed = true

      const builder = useBuilderStore()
      builder.setSiteData(asBuilderSiteData(JSON.parse(JSON.stringify(schema)) as SiteSchema))
      builder.setActivePageSlug('home')
      builder.markDirty()
      return schema
    },

    loadDraftSchema(): SiteSchema | null {
      const draft = readJson<SiteSchema>(SITE_DRAFT_KEY)
      if (!draft) return null
      try {
        validateSiteSchema(draft)
        return draft
      } catch {
        return null
      }
    },
  },
})
